import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { ArchiveMetricsResponse, LinearFit } from '../types';

type Props = {
  data: ArchiveMetricsResponse;
};

// Inner plot dimensions inside the SVG viewBox. The wrapper div makes the
// SVG fluid by width; the viewBox ratio controls the rendered aspect.
const VIEW_W = 800;
const VIEW_H = 280;
const PAD_L = 56;
const PAD_R = 16;
const PAD_T = 16;
const PAD_B = 36;

// Cap how far into the future the chart is willing to render. A completion
// ETA weeks/months out would otherwise stretch xMax (and the projected-pages
// y-axis) so far that the actual data collapses into a sliver. Past this we
// draw the projection only to the edge and show the ETA as a hovering "ETA…"
// annotation there instead — the real date stays in the tooltip + summary.
const MAX_FUTURE_SEC = 3 * 86400;

const COLOR_PAGES = '#2a7';
const COLOR_QUEUED = '#36c';
const COLOR_ERRORS = '#c33';
const COLOR_PROJ = '#888';
const COLOR_CROSS = '#a08';
// Theme-aware via CSS variables so the chart blends with light/dark
// page chrome. The series stroke colours stay fixed (the hues are
// chart-meaningful and read OK on either bg).
const COLOR_AXIS = 'var(--chart-axis)';
const COLOR_GRID = 'var(--chart-grid)';
const COLOR_TEXT = 'var(--chart-text)';
const COLOR_NOW_GUIDE = 'var(--chart-now-guide)';

function niceCeil(n: number): number {
  if (n <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(n)));
  const f = n / exp;
  // 1, 2, 5, 10 progression — keeps tick labels readable across orders
  // of magnitude.
  if (f <= 1) return 1 * exp;
  if (f <= 2) return 2 * exp;
  if (f <= 5) return 5 * exp;
  return 10 * exp;
}

function fmtTimestamp(epochSec: number, spanSec: number): string {
  const d = new Date(epochSec * 1000);
  // For windows under ~36h, show time-of-day; otherwise show the date so
  // ticks stay distinct across multi-day projections.
  if (spanSec < 36 * 3600) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function fmtFutureDate(epochSec: number): string {
  return new Date(epochSec * 1000).toLocaleString();
}

/** "in 3w 2d" / "in 4d 6h" / "in 2h 15m" — picks the two coarsest
 *  non-zero units so the eye lands on order of magnitude first. */
function fmtRelativeFuture(epochSec: number): string {
  const diffSec = epochSec - Date.now() / 1000;
  if (diffSec <= 60) return 'any moment now';

  const MIN = 60;
  const HOUR = 3600;
  const DAY = 86400;
  const WEEK = 7 * DAY;

  if (diffSec < HOUR) {
    const m = Math.max(1, Math.round(diffSec / MIN));
    return `in ${m} minute${m === 1 ? '' : 's'}`;
  }
  if (diffSec < DAY) {
    const h = Math.floor(diffSec / HOUR);
    const m = Math.round((diffSec % HOUR) / MIN);
    return m === 0 || h >= 6 ? `in ${h} hour${h === 1 ? '' : 's'}` : `in ${h}h ${m}m`;
  }
  if (diffSec < WEEK) {
    const d = Math.floor(diffSec / DAY);
    const h = Math.round((diffSec % DAY) / HOUR);
    return h === 0 ? `in ${d} day${d === 1 ? '' : 's'}` : `in ${d}d ${h}h`;
  }
  const w = Math.floor(diffSec / WEEK);
  const d = Math.round((diffSec % WEEK) / DAY);
  return d === 0 ? `in ${w} week${w === 1 ? '' : 's'}` : `in ${w}w ${d}d`;
}

function fmtTooltipTime(epochSec: number): string {
  const d = new Date(epochSec * 1000);
  return d.toLocaleString([], {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// Solve y at a given x using the linear fit.
function project(fit: LinearFit, x: number): number {
  return fit.slope * x + fit.intercept;
}

export default function ProgressChart({ data }: Props) {
  const view = useMemo(() => buildView(data), [data]);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [etaHovered, setEtaHovered] = useState(false);

  if (view === null) {
    return (
      <p className="muted" style={{ fontSize: 13 }}>
        Not enough samples yet. Trend will appear after the metrics scheduler
        has captured a few datapoints (every 10 min for active archives).
      </p>
    );
  }

  const {
    samples, xMin, xMax, yMax,
    pagesPath, queuedPath, errorsPath, pagesProjPath, queuedProjPath,
    eta, etaMarker, projection, latestX,
  } = view;

  const xTicks = makeXTicks(xMin, xMax);
  const yTicks = makeYTicks(yMax);

  const xScale = (x: number) => PAD_L + ((x - xMin) / (xMax - xMin)) * (VIEW_W - PAD_L - PAD_R);
  const yScale = (y: number) => VIEW_H - PAD_B - (y / yMax) * (VIEW_H - PAD_T - PAD_B);

  // Map a pointer event to the nearest sample index. The SVG scales with
  // its container via `width: 100%; height: auto`, so we have to convert
  // client coords back into viewBox space using the rendered bounding rect.
  function handlePointerMove(e: ReactPointerEvent<SVGRectElement>) {
    const svg = svgRef.current;
    if (!svg || samples.length === 0) return;
    const rect = svg.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * VIEW_W;

    // Past the latest sample (right gutter or extrapolation region) — snap
    // to the last sample so the rightmost data point is always easy to hit.
    if (svgX >= xScale(latestX)) {
      setHoverIdx(samples.length - 1);
      return;
    }

    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < samples.length; i++) {
      const dist = Math.abs(xScale(samples[i].x) - svgX);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }
    setHoverIdx(bestIdx);
  }

  const hover = hoverIdx !== null && hoverIdx < samples.length ? samples[hoverIdx] : null;

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8, fontSize: 13 }}>
        <LegendDot color={COLOR_PAGES} label="Pages fetched" />
        <LegendDot color={COLOR_QUEUED} label="Queued" />
        <LegendDot color={COLOR_ERRORS} label="Errors" />
        {projection.eta_at && <LegendDot color={COLOR_PROJ} label="Projection" dashed />}
        {projection.pages && (
          <span className="muted">
            +{ratePerHour(projection.pages.slope)} pages/hr
          </span>
        )}
        {projection.queued && (
          <span className="muted">
            {projection.queued.slope >= 0 ? '+' : ''}{ratePerHour(projection.queued.slope)} queue/hr
          </span>
        )}
      </div>

      <div style={{ width: '100%', overflow: 'hidden', position: 'relative' }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ width: '100%', height: 'auto', display: 'block' }}
        >
          {/* Y grid + labels */}
          {yTicks.map((y) => (
            <g key={`y${y}`}>
              <line
                x1={PAD_L} x2={VIEW_W - PAD_R}
                y1={yScale(y)} y2={yScale(y)}
                stroke={COLOR_GRID} strokeWidth={1}
              />
              <text
                x={PAD_L - 6} y={yScale(y) + 4}
                fontSize={11} textAnchor="end" fill={COLOR_TEXT}
              >
                {fmtCount(y)}
              </text>
            </g>
          ))}

          {/* X axis ticks + labels */}
          {xTicks.map((x) => (
            <g key={`x${x}`}>
              <line
                x1={xScale(x)} x2={xScale(x)}
                y1={VIEW_H - PAD_B} y2={VIEW_H - PAD_B + 4}
                stroke={COLOR_AXIS} strokeWidth={1}
              />
              <text
                x={xScale(x)} y={VIEW_H - PAD_B + 18}
                fontSize={11} textAnchor="middle" fill="#666"
              >
                {fmtTimestamp(x, xMax - xMin)}
              </text>
            </g>
          ))}

          {/* Axes */}
          <line
            x1={PAD_L} x2={VIEW_W - PAD_R}
            y1={VIEW_H - PAD_B} y2={VIEW_H - PAD_B}
            stroke={COLOR_AXIS} strokeWidth={1}
          />
          <line
            x1={PAD_L} x2={PAD_L}
            y1={PAD_T} y2={VIEW_H - PAD_B}
            stroke={COLOR_AXIS} strokeWidth={1}
          />

          {/* Vertical "now" guide separating actual from projection */}
          {projection.eta_at && (
            <line
              x1={xScale(latestX)} x2={xScale(latestX)}
              y1={PAD_T} y2={VIEW_H - PAD_B}
              stroke={COLOR_NOW_GUIDE} strokeWidth={1} strokeDasharray="2 3"
            />
          )}

          {/* Projected segments (dashed) */}
          {pagesProjPath && (
            <path
              d={pagesProjPath}
              fill="none"
              stroke={COLOR_PAGES}
              strokeWidth={2}
              strokeDasharray="4 4"
              opacity={0.7}
            />
          )}
          {queuedProjPath && (
            <path
              d={queuedProjPath}
              fill="none"
              stroke={COLOR_QUEUED}
              strokeWidth={2}
              strokeDasharray="4 4"
              opacity={0.7}
            />
          )}

          {/* Actual series */}
          <path d={errorsPath} fill="none" stroke={COLOR_ERRORS} strokeWidth={2} />
          <path d={queuedPath} fill="none" stroke={COLOR_QUEUED} strokeWidth={2} />
          <path d={pagesPath} fill="none" stroke={COLOR_PAGES} strokeWidth={2} />

          {/* Sample dots — only render if not too many to keep noise down */}
          {samples.length <= 80 && samples.map((s) => (
            <g key={s.x}>
              <circle cx={xScale(s.x)} cy={yScale(s.pages)} r={2} fill={COLOR_PAGES} />
              <circle cx={xScale(s.x)} cy={yScale(s.queued)} r={2} fill={COLOR_QUEUED} />
              <circle cx={xScale(s.x)} cy={yScale(s.errors)} r={2} fill={COLOR_ERRORS} />
            </g>
          ))}

          {/* Hover guide + highlighted points */}
          {hover && (
            <g pointerEvents="none">
              <line
                x1={xScale(hover.x)} x2={xScale(hover.x)}
                y1={PAD_T} y2={VIEW_H - PAD_B}
                stroke="#999" strokeWidth={1} strokeDasharray="3 3"
              />
              <circle cx={xScale(hover.x)} cy={yScale(hover.pages)} r={4}
                fill="#fff" stroke={COLOR_PAGES} strokeWidth={2} />
              <circle cx={xScale(hover.x)} cy={yScale(hover.queued)} r={4}
                fill="#fff" stroke={COLOR_QUEUED} strokeWidth={2} />
              <circle cx={xScale(hover.x)} cy={yScale(hover.errors)} r={4}
                fill="#fff" stroke={COLOR_ERRORS} strokeWidth={2} />
            </g>
          )}

          {/* Mouse capture overlay — sits above the series so sample
              snapping works everywhere in the plot area. */}
          <rect
            x={PAD_L} y={PAD_T}
            width={VIEW_W - PAD_L - PAD_R}
            height={VIEW_H - PAD_T - PAD_B}
            fill="transparent"
            onPointerMove={handlePointerMove}
            onPointerLeave={() => setHoverIdx(null)}
          />

          {/* ETA marker — drawn AFTER the capture rect so the dot owns
              its hit area (otherwise the rect swallows the pointer).
              Sits where the queued projection hits y=0, the operational
              "done" point. */}
          {etaMarker && (
            <g>
              <circle
                cx={xScale(etaMarker.x)} cy={yScale(etaMarker.y)}
                r={5} fill="#fff" stroke={COLOR_CROSS} strokeWidth={2}
                style={{ cursor: 'help' }}
                onPointerEnter={() => setEtaHovered(true)}
                onPointerLeave={() => setEtaHovered(false)}
              />
              <text
                x={xScale(etaMarker.x)} y={yScale(etaMarker.y) - 10}
                fontSize={11} fill={COLOR_CROSS}
                textAnchor={etaMarker.clamped ? 'end' : 'middle'}
                fontWeight={600}
                pointerEvents="none"
              >
                {etaMarker.clamped ? 'ETA…' : 'ETA'}
              </text>
            </g>
          )}
        </svg>

        {hover && (
          <HoverTooltip
            sample={hover}
            xPct={(xScale(hover.x) / VIEW_W) * 100}
            yPct={(Math.min(yScale(hover.pages), yScale(hover.queued), yScale(hover.errors)) / VIEW_H) * 100}
          />
        )}

        {eta && etaMarker && etaHovered && (
          <EtaTooltip
            label={fmtFutureDate(eta.x)}
            relative={fmtRelativeFuture(eta.x)}
            xPct={(xScale(etaMarker.x) / VIEW_W) * 100}
            yPct={(yScale(etaMarker.y) / VIEW_H) * 100}
          />
        )}
      </div>

      {projection.eta_at && eta ? (
        <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
          Projected completion: <strong>{fmtFutureDate(eta.x)}</strong>
          {' '}({fmtRelativeFuture(eta.x)}) at ~{fmtCount(eta.pagesAt)} pages.
          {' '}Linear extrapolation from the last 24h — treat as a rough
          estimate; queue can grow as deeper pages are discovered.
        </p>
      ) : (
        <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
          No completion projection: {projectionWhyNot(projection)}.
        </p>
      )}
    </div>
  );
}

function HoverTooltip({ sample, xPct, yPct }: { sample: ViewSample; xPct: number; yPct: number }) {
  // Flip the tooltip to the left of the cursor on the right half of the
  // chart so it never gets clipped by the wrapper's overflow:hidden.
  const flipLeft = xPct > 60;
  return (
    <div
      style={{
        position: 'absolute',
        left: `${xPct}%`,
        top: `${yPct}%`,
        transform: flipLeft
          ? 'translate(calc(-100% - 10px), -50%)'
          : 'translate(10px, -50%)',
        pointerEvents: 'none',
        background: 'var(--bg-tooltip)',
        border: '1px solid var(--border-tooltip)',
        borderRadius: 4,
        padding: '6px 8px',
        fontSize: 12,
        lineHeight: 1.4,
        boxShadow: 'var(--shadow-tooltip)',
        whiteSpace: 'nowrap',
        color: 'var(--fg)',
      }}
    >
      <div className="muted" style={{ fontSize: 11, marginBottom: 2 }}>
        {fmtTooltipTime(sample.x)}
      </div>
      <div>
        <span style={{ color: COLOR_PAGES, fontWeight: 600 }}>● </span>
        Pages: {sample.pages.toLocaleString()}
      </div>
      <div>
        <span style={{ color: COLOR_QUEUED, fontWeight: 600 }}>● </span>
        Queued: {sample.queued.toLocaleString()}
      </div>
      <div>
        <span style={{ color: COLOR_ERRORS, fontWeight: 600 }}>● </span>
        Errors: {sample.errors.toLocaleString()}
      </div>
    </div>
  );
}

/** Compact tooltip pinned above the ETA dot. Sits inside the same
 *  position:relative wrapper as HoverTooltip so percentage offsets line
 *  up with the SVG viewBox. */
function EtaTooltip({ label, relative, xPct, yPct }: { label: string; relative: string; xPct: number; yPct: number }) {
  const flipLeft = xPct > 60;
  return (
    <div
      style={{
        position: 'absolute',
        left: `${xPct}%`,
        top: `${yPct}%`,
        transform: flipLeft
          ? 'translate(calc(-100% - 12px), -100%)'
          : 'translate(12px, -100%)',
        pointerEvents: 'none',
        background: 'var(--bg-tooltip)',
        border: '1px solid var(--border-tooltip)',
        borderRadius: 4,
        padding: '6px 8px',
        fontSize: 12,
        lineHeight: 1.4,
        boxShadow: 'var(--shadow-tooltip)',
        whiteSpace: 'nowrap',
        color: 'var(--fg)',
      }}
    >
      <div className="muted" style={{ fontSize: 11, marginBottom: 2 }}>Projected completion</div>
      <div style={{ fontWeight: 600 }}>{label}</div>
      <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{relative}</div>
    </div>
  );
}

function LegendDot({ color, label, dashed = false }: { color: string; label: string; dashed?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <svg width={20} height={6} style={{ display: 'block' }}>
        <line x1={0} x2={20} y1={3} y2={3} stroke={color} strokeWidth={2}
          strokeDasharray={dashed ? '4 3' : undefined} />
      </svg>
      <span>{label}</span>
    </span>
  );
}

function projectionWhyNot(p: ArchiveMetricsResponse['projection']): string {
  if (p.samples_used < 3) return `only ${p.samples_used} sample(s) in the last 24h`;
  if (!p.queued) return 'queue series not available';
  if (p.queued.slope > 0) return 'queue is still growing — crawler is discovering faster than it drains';
  if (p.queued.slope === 0) return 'queue is flat — no net progress to extrapolate';
  return 'projected completion is too far out (>90d) or in the past';
}

function ratePerHour(slopePerSec: number): string {
  return Math.round(slopePerSec * 3600).toLocaleString();
}

function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return Math.round(n).toLocaleString();
}

function makeXTicks(min: number, max: number): number[] {
  const n = 5;
  const step = (max - min) / (n - 1);
  return Array.from({ length: n }, (_, i) => min + i * step);
}

function makeYTicks(yMax: number): number[] {
  const step = niceCeil(yMax / 5);
  const ticks: number[] = [];
  for (let v = 0; v <= yMax + 0.0001; v += step) ticks.push(v);
  return ticks;
}

type ViewSample = { x: number; pages: number; queued: number; errors: number };

type View = {
  samples: ViewSample[];
  xMin: number;
  xMax: number;
  yMax: number;
  pagesPath: string;
  queuedPath: string;
  errorsPath: string;
  pagesProjPath: string | null;
  queuedProjPath: string | null;
  // True ETA (queued→0), for the tooltip + summary text — may be off-screen.
  eta: { x: number; pagesAt: number } | null;
  // Where to DRAW the ETA marker: at the true ETA when it's within the
  // window, else pinned to the clamped right edge with clamped=true (label
  // becomes "ETA…"). y is the data-space value the dot sits on.
  etaMarker: { x: number; y: number; clamped: boolean } | null;
  projection: ArchiveMetricsResponse['projection'];
  latestX: number;
};

function buildView(data: ArchiveMetricsResponse): View | null {
  const { samples: rawSamples, projection } = data;
  if (rawSamples.length < 2) return null;

  const samples: ViewSample[] = rawSamples.map((s) => ({
    x: Date.parse(s.sampled_at) / 1000,
    pages: s.pages_fetched,
    queued: s.queued_count,
    errors: s.errors_count ?? 0,
  }));

  const latestX = samples[samples.length - 1].x;
  const earliestX = samples[0].x;

  // True ETA point (queued line projected to zero). Kept for the tooltip and
  // the summary line even when it's beyond the visible window.
  let eta: { x: number; pagesAt: number } | null = null;
  if (projection.eta_at) {
    const xEta = Date.parse(projection.eta_at) / 1000;
    const pagesAt = projection.eta_pages
      ?? (projection.pages ? project(projection.pages, xEta) : samples[samples.length - 1].pages);
    eta = { x: xEta, pagesAt };
  }

  // Clamp the future window: a far-out ETA must not zoom the chart out. When
  // clamped, the projection runs only to latestX + MAX_FUTURE_SEC and the ETA
  // marker pins to that edge ("ETA…"); otherwise rightEdgeX === eta.x and the
  // old behaviour (line lands on completion) is preserved exactly.
  const etaClamped = eta !== null && (eta.x - latestX) > MAX_FUTURE_SEC;
  const rightEdgeX = eta ? (etaClamped ? latestX + MAX_FUTURE_SEC : eta.x) : latestX;

  const xMin = earliestX;
  // Right-side gutter: with no extrapolation, samples often cluster near
  // the latest point, making the final dot hard to target. Extending xMax
  // past the right edge gives the cursor a hover-friendly margin; the snap
  // logic treats anything past latestX as the last sample.
  const xMax = eta
    ? rightEdgeX + (rightEdgeX - latestX) * 0.05
    : latestX + (latestX - earliestX) * 0.05;

  // Projection values at the visible right edge. When clamped these are the
  // 3-days-out values (queued still > 0, pages below the far-future total),
  // so neither axis blows out. When not clamped rightEdgeX === eta.x, so
  // queued lands on 0 and pages on eta.pagesAt — unchanged.
  const pagesAtEdge = projection.pages
    ? project(projection.pages, rightEdgeX)
    : (eta?.pagesAt ?? samples[samples.length - 1].pages);
  const queuedAtEdge = projection.queued ? Math.max(0, project(projection.queued, rightEdgeX)) : 0;

  const observedMax = Math.max(
    ...samples.map((s) => Math.max(s.pages, s.queued, s.errors)),
  );
  // Scale y to the projected pages at the (possibly clamped) right edge, not
  // at the far-future completion point.
  const projectedMax = eta ? pagesAtEdge : 0;
  const yMaxRaw = Math.max(observedMax, projectedMax, 1);
  const yMax = niceCeil(yMaxRaw * 1.1);

  const sx = (x: number) => PAD_L + ((x - xMin) / (xMax - xMin)) * (VIEW_W - PAD_L - PAD_R);
  const sy = (y: number) => VIEW_H - PAD_B - (y / yMax) * (VIEW_H - PAD_T - PAD_B);

  const pagesPath = samples.map((s, i) => `${i === 0 ? 'M' : 'L'} ${sx(s.x).toFixed(2)} ${sy(s.pages).toFixed(2)}`).join(' ');
  const queuedPath = samples.map((s, i) => `${i === 0 ? 'M' : 'L'} ${sx(s.x).toFixed(2)} ${sy(s.queued).toFixed(2)}`).join(' ');
  const errorsPath = samples.map((s, i) => `${i === 0 ? 'M' : 'L'} ${sx(s.x).toFixed(2)} ${sy(s.errors).toFixed(2)}`).join(' ');

  let pagesProjPath: string | null = null;
  let queuedProjPath: string | null = null;
  if (eta && projection.pages && projection.queued) {
    // Both projections run from the last sample to the visible right edge.
    pagesProjPath = `M ${sx(latestX).toFixed(2)} ${sy(project(projection.pages, latestX)).toFixed(2)} L ${sx(rightEdgeX).toFixed(2)} ${sy(pagesAtEdge).toFixed(2)}`;
    queuedProjPath = `M ${sx(latestX).toFixed(2)} ${sy(project(projection.queued, latestX)).toFixed(2)} L ${sx(rightEdgeX).toFixed(2)} ${sy(queuedAtEdge).toFixed(2)}`;
  }

  // Marker sits where the queued projection exits the view: y=0 at the true
  // ETA when on-screen, else the (still-positive) queued value at the clamped
  // edge, so "ETA…" hovers just above the rightmost projected point.
  const etaMarker = eta
    ? { x: rightEdgeX, y: etaClamped ? queuedAtEdge : 0, clamped: etaClamped }
    : null;

  return {
    samples,
    xMin,
    xMax,
    yMax,
    pagesPath,
    queuedPath,
    errorsPath,
    pagesProjPath,
    queuedProjPath,
    eta,
    etaMarker,
    projection,
    latestX,
  };
}
